/**
 * AdminFeesBox — "Service Fee Collection" card (admin fees or commission mode).
 * Extracted from CompanyRegistrationDetailPage.jsx during the iter-238 refactor.
 */
import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Percent, Loader2, Upload } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { SectionCard, OptionRow, SmartSaveButton, ReceiptDownloadLink } from './ui';
import { fileToDataUrl, extractReceipt } from './helpers';

export function AdminFeesBox({ reg, onSave, t, saving }) {
  const af = reg.admin_fees || {};
  // iter-237 (Feb 2026) — no default selection: `mode` is null until the
  // user explicitly picks "admin_fees" or "commission". Prevents accidental
  // defaults from being saved without an intentional choice.
  const afSaved = !!(reg.admin_fees && reg.admin_fees.mode);
  const [mode, setMode] = useState(afSaved ? af.mode : null);
  const [amount, setAmount] = useState(af.amount || '');
  const [reference, setReference] = useState(af.reference_number || '');
  const [receiptUrl, setReceiptUrl] = useState(af.receipt_url || '');
  const [receiptMime, setReceiptMime] = useState(af.receipt_mime || '');
  const [aiVerified, setAiVerified] = useState(!!af.ai_verified);
  const [scanning, setScanning] = useState(false);
  const [aiInfo, setAiInfo] = useState('');

  // iter-264c — dirty tracking for the SmartSaveButton.
  const dirty = useMemo(() => (
    (mode || null) !== (afSaved ? (af.mode || null) : null) ||
    String(amount || '') !== String(af.amount || '') ||
    (reference || '') !== (af.reference_number || '') ||
    (receiptUrl || '') !== (af.receipt_url || '') ||
    (receiptMime || '') !== (af.receipt_mime || '') ||
    !!aiVerified !== !!af.ai_verified
  ), [mode, amount, reference, receiptUrl, receiptMime, aiVerified, afSaved, af.mode, af.amount, af.reference_number, af.receipt_url, af.receipt_mime, af.ai_verified]);

  // Reset commission-only fields when switching to admin_fees mode so the
  // previous AI receipt for a commission doesn't linger when the operator
  // decides they want to charge a plain admin fee instead.
  const switchMode = (m) => {
    setMode(m);
    if (m === 'admin_fees') {
      setReference(''); setReceiptUrl(''); setReceiptMime(''); setAiVerified(false); setAiInfo('');
    }
  };

  const handleReceipt = async (file) => {
    if (!file) return;
    const url = await fileToDataUrl(file);
    setReceiptUrl(url); setReceiptMime(file.type);
    setScanning(true); setAiInfo(''); setAiVerified(false);
    try {
      // iter-225 (Feb 2026) — Commission proof is usually the tax invoice
      // CLOUDGM issues to the channel partner (RAKEZ). Pass "commission"
      // so the backend uses the lenient OCR prompt that also accepts
      // INVOICE / TAX INVOICE, not just payment receipts.
      const data = await extractReceipt(url, file.type, null, mode === 'commission' ? 'commission' : null);
      if (!data.is_receipt) {
        setAiInfo(t('❌ الملف ليس إيصالاً: ', '❌ Not a receipt: ') + (data.reject_reason || ''));
        toast.error(t('الملف ليس إيصالاً', 'File is not a receipt'));
      } else {
        if (data.amount) setAmount(String(data.amount));
        if (data.reference) setReference(String(data.reference));
        setAiVerified(true);
        setAiInfo(t('✓ AI قرأ الإيصال', '✓ AI read the receipt'));
        toast.success(t('✓ تم قراءة الإيصال', '✓ Receipt scanned'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل قراءة الإيصال', 'OCR failed'));
    } finally {
      setScanning(false);
    }
  };

  const save = () => {
    if (!mode) {
      toast.error(t('اختر: الرسوم الخاصة بنا أو كومشن', 'Pick: Our fees or Commission'));
      return;
    }
    if (mode === 'admin_fees') {
      if (!amount || parseFloat(amount) <= 0) {
        toast.error(t('المبلغ مطلوب', 'Amount required'));
        return;
      }
      onSave({ admin_fees: { mode, amount: parseFloat(amount) } });
    } else {
      // commission
      if (!receiptUrl) { toast.error(t('الكومشن: الإيصال مطلوب', 'Commission: receipt required')); return; }
      if (!amount || parseFloat(amount) <= 0) { toast.error(t('الكومشن: المبلغ مطلوب', 'Commission: amount required')); return; }
      if (!reference || !reference.trim()) { toast.error(t('الكومشن: رقم المرجع مطلوب', 'Commission: reference required')); return; }
      onSave({ admin_fees: {
        mode, amount: parseFloat(amount), reference_number: reference,
        receipt_url: receiptUrl, receipt_mime: receiptMime, ai_verified: aiVerified,
      }});
    }
  };

  return (
    <SectionCard icon={Percent} iconColor="#f5a55a" title={t('تحصيل الربح', 'Profit Collection')}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <OptionRow selected={mode === 'admin_fees'} onSelect={() => switchMode('admin_fees')} label={t('الرسوم الخاصة بنا فوق سعر الرخصة', 'Our fees above license price')} strike={mode === 'commission'} />
        <OptionRow selected={mode === 'commission'} onSelect={() => switchMode('commission')} label={t('كومشن (Commission)', 'Commission')} strike={mode === 'admin_fees'} />
      </div>

      {mode === 'admin_fees' ? (
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-xs text-[#71717A] mb-2">{t('مبلغ الرسوم الخاصة بنا (د.إ) *', 'Your service amount (AED) *')}</label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="admin-fees-amount" />
            <p className="text-[11px] text-[#71717A] mt-1">
              {t('لا يحتاج إيصال — هذا هو مبلغ خدمتك الخاص.', 'No receipt needed — this is your own service charge.')}
            </p>
          </div>
        </div>
      ) : mode === 'commission' ? (
        <>
          {/* iter-230 (Feb 2026) — Commission % helper.
              Base = License fees (this is the RAKEZ license price on which
              CLOUDGM's channel-partner commission % is computed). The
              percentage is auto-derived from (amount / base) × 100 so the
              operator immediately sees what commission RATE this invoice
              represents against the license price. */}
          {(() => {
            const licenseBase = Number(reg?.license_fees?.amount || 0);
            const amtN = parseFloat(amount);
            const pct = licenseBase > 0 && amtN > 0
              ? ((amtN / licenseBase) * 100)
              : null;
            return (
              <div className="mb-3 rounded-lg bg-[#f5a55a]/10 border border-[#f5a55a]/30 p-3 flex items-center gap-3 flex-wrap">
                <Percent className="w-4 h-4 text-[#f5a55a] flex-shrink-0" />
                <div className="text-xs text-white/70">
                  {t('الأساس (رسوم الرخصة)', 'Base (license fees)')}: {' '}
                  <span className="font-mono text-white font-bold">
                    {licenseBase > 0 ? licenseBase.toLocaleString() : '—'} AED
                  </span>
                </div>
                {licenseBase <= 0 && (
                  <span className="text-[11px] text-amber-300">
                    {t('أدخل رسوم الرخصة أولاً لحساب النسبة', 'Enter license fees first to compute %')}
                  </span>
                )}
                {pct !== null && (
                  <div className="text-xs text-white ms-auto font-bold" data-testid="commission-percent-display">
                    {t('نسبة العمولة', 'Commission %')}:{' '}
                    <span className="text-[#f5a55a] text-sm">{pct.toFixed(2)}%</span>
                  </div>
                )}
              </div>
            );
          })()}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-[#71717A] mb-2">{t('إيصال الكومشن *', 'Commission receipt *')}</label>
              <label className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#0a0a0a] border border-[#2a2a4e] text-white cursor-pointer text-sm">
                {scanning ? <Loader2 className="h-4 w-4 animate-spin text-[#f5a55a]" /> : <Upload className="h-4 w-4 text-[#f5a55a]" />}
                {scanning ? t('يقرأ AI...', 'AI reading...') : receiptUrl ? t('تم الرفع ✓', 'Uploaded ✓') : t('رفع', 'Upload')}
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => handleReceipt(e.target.files?.[0])} />
              </label>
              {aiInfo ? <p className="text-xs mt-1" style={{ color: aiVerified ? '#10b981' : '#ef4444' }}>{aiInfo}</p> : null}
              {af.receipt_url && af.mode === 'commission' ? (
                <ReceiptDownloadLink
                  url={af.receipt_url}
                  filename={`commission-receipt-${reg.request_code || reg.id}${(af.receipt_mime || '').includes('pdf') ? '.pdf' : '.jpg'}`}
                  label={t('إيصال الكومشن المحفوظ', 'Saved commission receipt')}
                  color="#f5a55a"
                  testId="commission-receipt"
                />
              ) : null}
            </div>
            <div>
              <label className="block text-xs text-[#71717A] mb-2">{t('مبلغ الكومشن (د.إ) *', 'Commission amount (AED) *')}</label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" data-testid="commission-amount" />
            </div>
            <div>
              <label className="block text-xs text-[#71717A] mb-2">{t('النسبة (%)', 'Percentage (%)')}</label>
              <Input
                type="text"
                readOnly
                value={(() => {
                  const licenseBase = Number(reg?.license_fees?.amount || 0);
                  const amtN = parseFloat(amount);
                  if (licenseBase <= 0 || !amtN) return '';
                  return `${((amtN / licenseBase) * 100).toFixed(2)}%`;
                })()}
                placeholder="—"
                className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl cursor-not-allowed"
                dir="ltr"
                data-testid="commission-percent"
              />
            </div>
            <div>
              <label className="block text-xs text-[#71717A] mb-2">{t('رقم المرجع *', 'Reference *')}</label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)} className="bg-[#0a0a0a] border-[#2a2a4e] text-white rounded-xl" dir="ltr" />
            </div>
          </div>
        </>
      ) : null}

      <div className="mt-4 text-end">
        <SmartSaveButton
          dirty={dirty}
          saving={saving}
          onClick={save}
          t={t}
          color="#f5a55a"
          hoverColor="#f97316"
          data-testid="admin-fees-save"
        />
      </div>
    </SectionCard>
  );
}
